import { queueMacrotask } from 'module/helpers/macrotask';
import { useTranslate } from 'module/hooks/use-translation';
import {
  DefaultCustomLayoutDrawerProps,
  Drawer,
} from 'module/ui/components/atoms/drawer';
import { Form } from 'module/ui/components/atoms/form';
import { toast } from '@rocketlane/ui';
import { KnowledgeBaseFolderLite } from 'api/knowledge-base/types';
import { useState, useEffect, FC, useCallback } from 'react';
import { AddKnowledgeTriggerType } from '../add-knowledge-trigger';
import { FolderContentCreateStep } from './folder-content-create-step';
import { FolderCreateStep, FolderStepFormValues } from './folder-create-step';
import { AiPagesPayload } from '../add-ai-pages/ai-pages';
import { AddFilesPayload } from '../add-files';
import { AddWebsitePayload } from '../add-website';

enum Step {
  FolderCreate = 'folder-create',
  FolderContent = 'folder-content',
}

export type KnowledgeBaseFolderDrawerPayload = {
  folderName: string;
  folderDescription?: string;
  contentType?: AddKnowledgeTriggerType;
  contentPayload?: AiPagesPayload | AddWebsitePayload | AddFilesPayload;
};

interface Props {
  open: boolean;
  onClose: () => void;
  onDone: (payload: KnowledgeBaseFolderDrawerPayload) => Promise<void>;
  folder?: KnowledgeBaseFolderLite | null;
}

export const CreateKnowledgeBaseFolderDrawer: FC<Props> = ({
  open,
  onClose,
  onDone,
  folder,
}) => {
  const [folderStepForm] = Form.useForm<FolderStepFormValues>();
  const [actionLoading, setActionLoading] = useState(false);
  const [folderStepFormValid, setFolderStepFormValid] = useState(false);
  const [currentStep, setCurrentStep] = useState<Step>(Step.FolderCreate);
  const isEdit = folder != null;
  const translate = useTranslate();

  useEffect(() => {
    if (open) {
      if (folder) {
        folderStepForm.setFieldsValue({
          folderName: folder.folderName,
          folderDescription: folder.folderDescription,
        });
        setFolderStepFormValid(folder.folderName.trim().length > 0);
      } else {
        folderStepForm.resetFields();
        setFolderStepFormValid(false);
      }
      setActionLoading(false);
      setCurrentStep(Step.FolderCreate);
    }
  }, [open, folder, folderStepForm]);

  const onFolderStepFormValuesChange = useCallback(() => {
    queueMacrotask(() => {
      const folderName =
        folderStepForm.getFieldValue('folderName')?.trim() ?? '';

      setFolderStepFormValid(folderName.length > 0);
    });
  }, [folderStepForm]);

  const getFolderFields = useCallback(() => {
    return {
      folderName: folderStepForm.getFieldValue('folderName')?.trim() ?? '',
      folderDescription:
        folderStepForm.getFieldValue('folderDescription')?.trim() ?? '',
    };
  }, [folderStepForm]);

  const submit = useCallback(
    async (payload: KnowledgeBaseFolderDrawerPayload) => {
      setActionLoading(true);
      try {
        await onDone(payload);
      } finally {
        setActionLoading(false);
      }
    },
    [onDone],
  );

  const onFolderCreateComplete = useCallback(() => {
    folderStepForm
      .validateFields()
      .then(async () => {
        if (isEdit) {
          try {
            await submit(getFolderFields());
          } catch {
            toast({
              title: translate('knowledge_base.folder.update.error'),
              variant: 'error',
            });
          }

          return;
        }
        setCurrentStep(Step.FolderContent);
      })
      .catch(() => {});
  }, [folderStepForm, getFolderFields, isEdit, submit, translate]);

  const onFolderContentCreateComplete = useCallback(
    async (payload: AiPagesPayload | AddWebsitePayload | AddFilesPayload) => {
      try {
        await submit({
          ...getFolderFields(),
          contentType: folderStepForm.getFieldValue('contentType'),
          contentPayload: payload,
        });
      } catch {
        toast({
          title: translate('knowledge_base.folder.create.error'),
          variant: 'error',
        });
      }
    },
    [folderStepForm, getFolderFields, submit, translate],
  );

  return (
    <Drawer
      width="672px"
      open={open}
      onClose={onClose}
      {...DefaultCustomLayoutDrawerProps}
    >
      {currentStep === Step.FolderCreate && (
        <FolderCreateStep
          form={folderStepForm}
          mode={isEdit ? 'edit' : 'create'}
          actionLoading={actionLoading}
          onFormValuesChange={onFolderStepFormValuesChange}
          onClose={onClose}
          onNext={onFolderCreateComplete}
          isFormValid={folderStepFormValid}
        />
      )}
      {!isEdit && currentStep === Step.FolderContent && (
        <FolderContentCreateStep
          folderCreateStepForm={folderStepForm}
          onClose={onClose}
          onNext={onFolderContentCreateComplete}
          actionLoading={actionLoading}
        />
      )}
    </Drawer>
  );
};
